import { useCallback, useEffect, useState } from 'react'
import { api, ErroApi, type Profissional } from '../lib/api'
import { Secao, Erro, Carregando, Vazio } from '../componentes/ui'

/**
 * Seções de configuração que ainda não tinham tela.
 *
 * Todas usam API que já existia — o que faltava era interface. Elas
 * moram fora de `Config.tsx` porque aquele arquivo já carrega os
 * formulários de recurso, profissional e procedimento; juntar tudo
 * daria um arquivo de novecentas linhas que ninguém abre com prazer.
 */

/* ══ como a grade se comporta ═════════════════════════════════════ */

type Grade = {
  grade_min: number
  passo_oferta_min: number
  duracao_minima: number
  tolerancia_no_show_min: number
  permite_overbook: boolean
}

const PASSOS = [5, 10, 15, 20, 30, 45, 60] as const

export function SecaoGrade({ podeEditar }: { podeEditar: boolean }) {
  const [g, setG] = useState<Grade | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)
  const [salvo, setSalvo] = useState(false)

  useEffect(() => {
    void (async () => {
      try { setG(await api<Grade>('/v1/config-agenda')) }
      catch (e) { setErro(e instanceof ErroApi ? e.message : 'Não foi possível carregar.') }
      finally { setCarregando(false) }
    })()
  }, [])

  async function salvar(mudanca: Partial<Grade>) {
    if (!g || !podeEditar) return
    const novo = { ...g, ...mudanca }
    setG(novo); setSalvando(true); setSalvo(false); setErro(null)
    try {
      await api('/v1/config-agenda', { metodo: 'PUT', corpo: novo })
      setSalvo(true)
      window.setTimeout(() => setSalvo(false), 2200)
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível salvar.')
    } finally { setSalvando(false) }
  }

  return (
    <Secao
      titulo="Como a grade se comporta"
      descricao="Três números diferentes que costumam ser confundidos num só."
    >
      <Erro mensagem={erro} />
      {carregando ? <Carregando altura={140} /> : g && (
        <div className="cfg__grade">
          {/*
            O concorrente tem UM número — "tempo de visualização na
            agenda" — e com ele resolve desenho, oferta e duração ao
            mesmo tempo. Quem atende em 50 minutos precisa arredondar
            para 45 ou 60. Aqui são três coisas distintas porque são
            três perguntas distintas.
          */}
          <div className="campo">
            <label htmlFor="cf-grade">A grade desenha de quantos em quantos minutos</label>
            <select id="cf-grade" className="entrada" disabled={!podeEditar}
                    value={g.grade_min}
                    onChange={(e) => void salvar({ grade_min: Number(e.target.value) })}>
              {PASSOS.map((p) => <option key={p} value={p}>{p} minutos</option>)}
            </select>
            <span className="ajuda">Só afeta o desenho da tela, não o que pode ser marcado.</span>
          </div>

          <div className="campo">
            <label htmlFor="cf-passo">Horários oferecidos de quantos em quantos</label>
            <select id="cf-passo" className="entrada" disabled={!podeEditar}
                    value={g.passo_oferta_min}
                    onChange={(e) => void salvar({ passo_oferta_min: Number(e.target.value) })}>
              {PASSOS.map((p) => <option key={p} value={p}>{p} minutos</option>)}
            </select>
            <span className="ajuda">
              Com 15, uma sessão de 50 min pode começar às 9:00, 9:15, 9:30…
            </span>
          </div>

          <div className="campo">
            <label htmlFor="cf-dur">Menor atendimento possível</label>
            <input id="cf-dur" className="entrada" type="number" min={5} max={240}
                   disabled={!podeEditar} value={g.duracao_minima}
                   onChange={(e) => void salvar({ duracao_minima: Number(e.target.value) })} />
            <span className="ajuda">Em minutos. Vale como piso, mesmo se o procedimento pedir menos.</span>
          </div>

          <div className="campo">
            <label htmlFor="cf-tol">Tolerância antes de marcar falta</label>
            <input id="cf-tol" className="entrada" type="number" min={0} max={180}
                   disabled={!podeEditar} value={g.tolerancia_no_show_min}
                   onChange={(e) => void salvar({ tolerancia_no_show_min: Number(e.target.value) })} />
            <span className="ajuda">
              Em minutos após o horário. Antes disso o atendimento continua esperando.
            </span>
          </div>

          <label className="cfg__switch">
            <input type="checkbox" disabled={!podeEditar} checked={g.permite_overbook}
                   onChange={(e) => void salvar({ permite_overbook: e.target.checked })} />
            <span>
              <strong>Permitir encaixe no mesmo horário</strong>
              {/*
                Desligado, a trava do banco recusa o choque. Ligado, ela
                continua existindo — o encaixe passa a ser uma exceção
                autorizada e registrada, não uma porta aberta. É a
                diferença entre a nossa trava e o "encaixe ilimitado"
                que os concorrentes documentam como recurso.
              */}
              <span className="ajuda">
                Mesmo ligado, o choque de sala continua sendo recusado pelo banco —
                o encaixe vira exceção autorizada e fica registrado.
              </span>
            </span>
          </label>

          {podeEditar && (
            <p className="ajuda cfg__salvo" role="status">
              {salvando ? 'Salvando…' : salvo ? '✓ Salvo' : 'Cada mudança salva sozinha.'}
            </p>
          )}
        </div>
      )}
    </Secao>
  )
}

/* ══ horários de trabalho ═════════════════════════════════════════ */

type Jornada = {
  id: string
  dono_tipo: 'PROFISSIONAL' | 'RECURSO'
  dono_id: string
  dia_semana: number
  inicio: string; fim: string
  tipo: string | null
}

const DIAS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado']

export function SecaoJornadas({ podeEditar }: { podeEditar: boolean }) {
  const [profs, setProfs] = useState<Profissional[]>([])
  const [quem, setQuem] = useState('')
  const [itens, setItens] = useState<Jornada[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [novo, setNovo] = useState({ dia: 1, inicio: '08:00', fim: '12:00' })

  useEffect(() => {
    void (async () => {
      try {
        const r = await api<{ itens: Profissional[] }>('/v1/profissionais')
        const ativos = r.itens.filter((p) => p.ativo)
        setProfs(ativos)
        if (ativos[0]) setQuem(ativos[0].id)
      } catch { /* a seção avisa que não há profissional */ }
      finally { setCarregando(false) }
    })()
  }, [])

  const carregar = useCallback(async () => {
    if (!quem) { setItens([]); return }
    setErro(null)
    try {
      const r = await api<{ itens: Jornada[] }>(
        `/v1/jornadas?dono_tipo=PROFISSIONAL&dono_id=${quem}`)
      setItens(r.itens)
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível carregar os horários.')
    }
  }, [quem])
  useEffect(() => { void carregar() }, [carregar])

  async function adicionar(e: React.FormEvent) {
    e.preventDefault()
    setErro(null)
    try {
      await api('/v1/jornadas', {
        metodo: 'POST',
        corpo: {
          dono_tipo: 'PROFISSIONAL', dono_id: quem,
          dia_semana: novo.dia, inicio: novo.inicio, fim: novo.fim,
        },
      })
      await carregar()
    } catch (err) {
      setErro(err instanceof ErroApi ? err.message : 'Não foi possível adicionar.')
    }
  }

  async function remover(id: string) {
    try { await api(`/v1/jornadas/${id}`, { metodo: 'DELETE' }); await carregar() }
    catch (e) { setErro(e instanceof ErroApi ? e.message : 'Não foi possível remover.') }
  }

  const porDia = DIAS.map((_, d) =>
    itens.filter((j) => j.dia_semana === d).sort((a, b) => a.inicio.localeCompare(b.inicio)))

  return (
    <Secao
      titulo="Horários de trabalho"
      descricao="Quantas faixas quiser por dia — manhã e tarde são duas faixas, e o almoço fica de fora sem precisar de bloqueio."
    >
      <Erro mensagem={erro} />
      {carregando ? <Carregando altura={120} /> : profs.length === 0 ? (
        <Vazio
          mensagem="Nenhum profissional cadastrado ainda."
          sugestao="Cadastre em “Quem trabalha aqui” para definir horários."
        />
      ) : (
        <>
          <div className="campo">
            <label htmlFor="jr-quem">Profissional</label>
            <select id="jr-quem" className="entrada" value={quem}
                    onChange={(e) => setQuem(e.target.value)}>
              {profs.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
            </select>
          </div>

          <ul className="cfg__dias">
            {porDia.map((faixas, d) => (
              <li key={d} className={faixas.length === 0 ? 'is-folga' : ''}>
                <strong>{DIAS[d]}</strong>
                {faixas.length === 0 ? (
                  <span className="ajuda">folga</span>
                ) : (
                  <div className="cfg__faixas">
                    {faixas.map((f) => (
                      <span key={f.id} className="cfg__faixa">
                        <span className="mono">{f.inicio.slice(0, 5)}–{f.fim.slice(0, 5)}</span>
                        {podeEditar && (
                          <button onClick={() => void remover(f.id)}
                                  aria-label={`Remover ${f.inicio} às ${f.fim} de ${DIAS[d]}`}>×</button>
                        )}
                      </span>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>

          {podeEditar && (
            <form className="cfg__nova-faixa" onSubmit={adicionar}>
              <label htmlFor="jr-dia" className="oculto-visual">Dia</label>
              <select id="jr-dia" className="entrada" value={novo.dia}
                      onChange={(e) => setNovo({ ...novo, dia: Number(e.target.value) })}>
                {DIAS.map((d, i) => <option key={d} value={i}>{d}</option>)}
              </select>
              <label htmlFor="jr-ini" className="oculto-visual">Início</label>
              <input id="jr-ini" className="entrada" type="time" required value={novo.inicio}
                     onChange={(e) => setNovo({ ...novo, inicio: e.target.value })} />
              <span className="ajuda">até</span>
              <label htmlFor="jr-fim" className="oculto-visual">Fim</label>
              <input id="jr-fim" className="entrada" type="time" required value={novo.fim}
                     onChange={(e) => setNovo({ ...novo, fim: e.target.value })} />
              <button className="botao botao--pequeno" type="submit">Adicionar faixa</button>
            </form>
          )}
        </>
      )}
    </Secao>
  )
}

/* ══ especialidades ═══════════════════════════════════════════════ */

type Especialidade = { id: string; nome: string; ativo: boolean }

export function SecaoEspecialidades({ podeEditar }: { podeEditar: boolean }) {
  const [itens, setItens] = useState<Especialidade[]>([])
  const [nome, setNome] = useState('')
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  const carregar = useCallback(async () => {
    try {
      const r = await api<{ itens: Especialidade[] }>('/v1/especialidades')
      setItens(r.itens)
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível carregar.')
    } finally { setCarregando(false) }
  }, [])
  useEffect(() => { void carregar() }, [carregar])

  /* Normaliza antes de comparar: "MASSOTERAPIA" e "Massoterapia" são a
     mesma coisa. No sistema do concorrente as duas convivem na mesma
     lista, e viram dois grupos distintos em todo relatório. */
  const jaExiste = (n: string) => {
    const chave = (s: string) =>
      s.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    return itens.some((i) => chave(i.nome) === chave(n))
  }

  async function adicionar(e: React.FormEvent) {
    e.preventDefault()
    const n = nome.trim()
    if (!n) return
    if (jaExiste(n)) {
      setErro(`“${n}” já existe na lista, com outra grafia ou acentuação.`)
      return
    }
    setErro(null)
    try {
      await api('/v1/especialidades', { metodo: 'POST', corpo: { nome: n } })
      setNome(''); await carregar()
    } catch (err) {
      setErro(err instanceof ErroApi ? err.message : 'Não foi possível adicionar.')
    }
  }

  return (
    <Secao
      titulo="Especialidades"
      descricao="Começa vazia de propósito — a lista pronta de outro sistema é o que faz uma clínica de estética ver “Endodontia” no primeiro acesso."
    >
      <Erro mensagem={erro} />
      {carregando ? <Carregando altura={90} /> : (
        <>
          {itens.length === 0 ? (
            <Vazio mensagem="Nenhuma especialidade cadastrada."
                   sugestao="Cadastre as que a clínica realmente atende." />
          ) : (
            <ul className="cfg__chips">
              {itens.map((i) => (
                <li key={i.id} className={i.ativo ? '' : 'is-inativo'}>{i.nome}</li>
              ))}
            </ul>
          )}

          {podeEditar && (
            <form className="cfg__inline" onSubmit={adicionar}>
              <label htmlFor="esp-nome" className="oculto-visual">Nova especialidade</label>
              <input id="esp-nome" className="entrada" maxLength={80} value={nome}
                     placeholder="Fisioterapia respiratória, por exemplo"
                     onChange={(e) => setNome(e.target.value)} />
              <button className="botao botao--pequeno" type="submit"
                      disabled={nome.trim().length < 2}>Adicionar</button>
            </form>
          )}
        </>
      )}
    </Secao>
  )
}
